import React, { useMemo, useState } from "react";
import { colors, radius, spacing, typography, cardBase } from "../../utils/designTokens";
import { useToast } from "../../components/Toast";
import { useIsMobile } from "../../hooks/useResponsive";
import { useProspeccionData } from "../../hooks/useProspeccionData";
import { STATUS_LABELS } from "../../utils/statusColors";
import { toJSDate } from "../../utils/dates";
import { datosEdificio } from "../../utils/datosEdificio";
import { estaAsignado, estaBloqueado, fmtFechaLiberacion } from "../../utils/crmAsignacion";
import { contactosDeCrm } from "../../utils/crmContactos";
import ScreenHeader from "../../components/ScreenHeader";
import KPICard from "../../components/KPICard";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import AppSelect from "../../components/AppSelect";
import CrmFicha from "../../components/CrmFicha";
import {
  FaHeadset, FaSearch, FaFileExcel, FaAddressCard, FaComments, FaHandshake, FaTrophy, FaLock,
} from "react-icons/fa";

const fmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("es-UY"));
const CRM_ACTIVOS = ["contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado"];
const CRM_OPTS = [{ value: "", label: "Estado: todos" }, ...CRM_ACTIVOS.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))];
const ORDEN_OPTS = [
  { value: "ultimaMs", label: "Últ. gestión" },
  { value: "direccion", label: "Dirección" },
  { value: "score", label: "Score" },
  { value: "presupuestoMonto", label: "Presupuesto" },
];

// "Contactado" por un comercial = tiene comercial asignado, estado avanzado,
// contactos, notas o un presupuesto. Es todo lo que amerita seguimiento.
function fueContactado(c) {
  return estaAsignado(c)
    || (c.estado && c.estado !== "sin_contactar")
    || contactosDeCrm(c).length > 0
    || (Array.isArray(c.notas) && c.notas.length > 0)
    || c.presupuestoMonto != null;
}

const fechaCorta = (v) => { const d = toJSDate(v); return d ? d.toLocaleDateString("es-UY", { day: "numeric", month: "short" }) : "—"; };

/**
 * Seguimiento comercial (SuperAdmin): todos los edificios que los comerciales
 * contactaron, con quién los gestiona, en qué estado, la próxima acción y la
 * última gestión — para hacerles seguimiento. Filtros por comercial/estado,
 * orden, búsqueda y export. Tocar una fila abre la ficha CRM (editar/reasignar).
 */
export default function SeguimientoComercial({ setSelected }) {
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { puntos, crmByPadron, comerciales, loading, updateCrm } = useProspeccionData();
  const [ficha, setFicha] = useState(null);
  const [busca, setBusca] = useState("");
  const [fComercial, setFComercial] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [orden, setOrden] = useState("ultimaMs");
  const [dir, setDir] = useState("desc");

  const puntoByPadron = useMemo(() => {
    const m = {}; for (const p of puntos) m[String(p.padron)] = p; return m;
  }, [puntos]);

  // Filas: un edificio contactado por comercial, con sus datos de seguimiento.
  const filas = useMemo(() => {
    const out = [];
    for (const c of Object.values(crmByPadron)) {
      if (!fueContactado(c)) continue;
      const ef = datosEdificio(puntoByPadron[String(c.padron)], c);
      out.push({
        id: String(c.padron), padron: c.padron,
        direccion: ef.direccion || `Padrón ${c.padron}`,
        nUnidades: ef.nUnidades,
        estado: c.estado || "sin_contactar",
        asignadoEmail: (c.asignadoEmail || "").toLowerCase(),
        asignadoA: c.asignadoA || "",
        crm: c,
        proximaAccion: c.proximaAccion || "",
        proximaAccionFecha: c.proximaAccionFecha || "",
        ultimaMs: toJSDate(c.ultimaGestionAt)?.getTime() || 0,
        presupuestoMonto: c.presupuestoMonto ?? null,
        score: puntoByPadron[String(c.padron)]?.score ?? c.score ?? 0,
        contactos: contactosDeCrm(c).length,
      });
    }
    return out;
  }, [crmByPadron, puntoByPadron]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    let arr = filas.filter((f) => {
      if (fComercial === "__sin__" && f.asignadoEmail) return false;         // solo sin asignar
      if (fComercial && fComercial !== "__sin__" && f.asignadoEmail !== fComercial) return false;
      if (fEstado && f.estado !== fEstado) return false;
      if (q && ![f.direccion, f.asignadoA].some((x) => String(x || "").toLowerCase().includes(q))) return false;
      return true;
    });
    arr = [...arr].sort((a, b) => {
      const av = a[orden], bv = b[orden];
      const c = typeof av === "string" ? String(av).localeCompare(String(bv)) : (Number(av || 0) - Number(bv || 0));
      return dir === "asc" ? c : -c;
    });
    return arr;
  }, [filas, busca, fComercial, fEstado, orden, dir]);

  const kpis = useMemo(() => {
    const k = { total: filas.length, activos: 0, propuesta: 0, ganado: 0 };
    for (const f of filas) {
      if (["contactado", "en_conversacion", "reunion"].includes(f.estado)) k.activos++;
      if (f.estado === "propuesta") k.propuesta++;
      if (f.estado === "ganado") k.ganado++;
    }
    return k;
  }, [filas]);

  function onSort(key) {
    if (orden === key) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setOrden(key); setDir(key === "direccion" ? "asc" : "desc"); }
  }
  function abrir(f) {
    const p = puntoByPadron[String(f.padron)] || { padron: f.padron, direccion: f.direccion };
    setFicha(p);
  }

  const comercialOpts = [
    { value: "", label: "Comercial: todos" },
    ...comerciales.map((c) => ({ value: c.email, label: c.name || c.email })),
    { value: "__sin__", label: "Sin asignar" },
  ];

  async function exportar() {
    try {
      const XLSX = await import("xlsx");
      const rows = filtradas.map((f) => ({
        Direccion: f.direccion, Unidades: f.nUnidades, Comercial: f.asignadoA || "(sin asignar)",
        Estado: STATUS_LABELS[f.estado] || f.estado, Contactos: f.contactos,
        ProximaAccion: f.proximaAccion, FechaProxima: f.proximaAccionFecha,
        UltimaGestion: f.ultimaMs ? new Date(f.ultimaMs).toLocaleDateString("es-UY") : "",
        Presupuesto: f.presupuestoMonto ?? "", Score: f.score,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Seguimiento");
      XLSX.writeFile(wb, "seguimiento-comercial.xlsx");
    } catch (e) {
      showToast("No se pudo exportar.", "error");
    }
  }

  const columns = [
    { key: "direccion", label: "Edificio", sortable: true, render: (v, r) => (
      <div>
        <div style={{ color: colors.gray900 }}>{v}</div>
        <div style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}>Padrón {r.padron} · {fmt(r.nUnidades)} unid.{r.contactos ? ` · ${r.contactos} contacto${r.contactos === 1 ? "" : "s"}` : ""}</div>
      </div>
    ) },
    { key: "asignadoA", label: "Comercial", render: (v, r) => (v ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        {v}{estaBloqueado(r.crm) && <span title={`Bloqueado hasta ${fmtFechaLiberacion(r.crm) || "—"}`} style={{ color: colors.gray400 }}><FaLock size={9} /></span>}
      </span>
    ) : <span style={{ color: colors.gray400 }}>sin asignar</span>) },
    { key: "estado", label: "Estado", render: (v) => <StatusBadge status={v} size="sm" /> },
    { key: "proximaAccion", label: "Próxima acción", render: (v, r) => (v || r.proximaAccionFecha ? (
      <div>
        {v && <div style={{ color: colors.gray700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>{v}</div>}
        {r.proximaAccionFecha && <div style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>{r.proximaAccionFecha}</div>}
      </div>
    ) : <span style={{ color: colors.gray400 }}>—</span>) },
    { key: "ultimaMs", label: "Últ. gestión", sortable: true, render: (v) => (v ? fechaCorta(v) : "—") },
    { key: "presupuestoMonto", label: "Presupuesto", align: "right", sortable: true, render: (v) => (v != null ? `$ ${fmt(v)}` : "—") },
  ];

  return (
    <div style={{ padding: isMobile ? spacing[3] : spacing[6], maxWidth: 1280, margin: "0 auto" }}>
      {!isMobile && (
        <ScreenHeader icon={FaHeadset} title="Seguimiento comercial" subtitle="Todos los edificios que contactaron los comerciales · seguimiento y reasignación" />
      )}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: spacing[3], marginBottom: spacing[4] }}>
        <KPICard icon={FaAddressCard} iconBg={colors.primaryLighter} iconColor={colors.primary} label="Contactados" value={fmt(kpis.total)} />
        <KPICard icon={FaComments} iconBg={colors.infoLight} iconColor={colors.infoDark} label="En conversación" value={fmt(kpis.activos)} />
        <KPICard icon={FaHandshake} iconBg={colors.warningLight} iconColor={colors.warningDark} label="Propuesta enviada" value={fmt(kpis.propuesta)} />
        <KPICard icon={FaTrophy} iconBg={colors.successLight} iconColor={colors.successDark} label="Ganados" value={fmt(kpis.ganado)} />
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap", marginBottom: spacing[4] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flex: "1 1 220px", minWidth: 200, background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: "0 10px" }}>
          <FaSearch size={12} color={colors.gray400} />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar edificio o comercial…" aria-label="Buscar"
            style={{ flex: 1, minWidth: 0, border: "none", outline: "none", padding: "10px 0", fontSize: typography.sizes.base, fontFamily: typography.fontFamily, background: "transparent", color: colors.gray900 }} />
        </div>
        <div style={{ width: 190 }}><AppSelect value={fComercial} onChange={setFComercial} options={comercialOpts} ariaLabel="Filtrar por comercial" /></div>
        <div style={{ width: 180 }}><AppSelect value={fEstado} onChange={setFEstado} options={CRM_OPTS} ariaLabel="Filtrar por estado" /></div>
        {isMobile && <div style={{ width: 160 }}><AppSelect value={orden} onChange={setOrden} options={ORDEN_OPTS.map((o) => ({ ...o, label: `Orden: ${o.label}` }))} ariaLabel="Ordenar" /></div>}
        {!isMobile && (
          <button onClick={exportar} style={ghostBtn} aria-label="Exportar Excel"><FaFileExcel size={13} /> Excel</button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: spacing[8], color: colors.gray600 }}>Cargando seguimiento…</div>
      ) : filtradas.length === 0 ? (
        <EmptyState icon={FaHeadset} title="Sin edificios contactados"
          description={busca || fComercial || fEstado ? "No hay resultados con estos filtros." : "Cuando los comerciales empiecen a gestionar edificios, aparecen acá para seguimiento."} />
      ) : isMobile ? (
        <div style={{ display: "grid", gap: spacing[2] }}>
          {filtradas.map((f) => (
            <div key={f.id} onClick={() => abrir(f)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && abrir(f)}
              style={{ ...cardBase, padding: spacing[3], cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.direccion}</span>
                <StatusBadge status={f.estado} size="sm" />
              </div>
              <div style={{ marginTop: 4, fontSize: typography.sizes.sm, color: colors.gray600, display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
                <span>{f.asignadoA || "sin asignar"}</span>
                {f.ultimaMs > 0 && <span>· últ. {fechaCorta(f.ultimaMs)}</span>}
                {f.presupuestoMonto != null && <span>· $ {fmt(f.presupuestoMonto)}</span>}
              </div>
              {f.proximaAccion && <div style={{ marginTop: 2, fontSize: typography.sizes.sm, color: colors.gray700 }}>→ {f.proximaAccion}{f.proximaAccionFecha ? ` (${f.proximaAccionFecha})` : ""}</div>}
            </div>
          ))}
        </div>
      ) : (
        <DataTable columns={columns} data={filtradas} sortKey={orden} sortDirection={dir} onSort={onSort} onRowClick={abrir} />
      )}

      <div style={{ marginTop: spacing[4], fontSize: typography.sizes.xs, color: colors.gray400 }}>
        {fmt(filtradas.length)} edificios en seguimiento. Tocá una fila para abrir su ficha, seguirlo o reasignarlo.
      </div>

      <CrmFicha
        open={!!ficha}
        onClose={() => setFicha(null)}
        padron={ficha?.padron}
        punto={ficha}
        crm={ficha ? crmByPadron[String(ficha.padron)] : null}
        comerciales={comerciales}
        onSaved={updateCrm}
        setSelected={setSelected}
      />
    </div>
  );
}

const ghostBtn = {
  display: "flex", alignItems: "center", gap: spacing[1], padding: `${spacing[2]}px ${spacing[3]}px`,
  background: "#fff", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  cursor: "pointer", fontSize: typography.sizes.sm, color: colors.gray700,
};
